import api, { handleError, handleResponse } from '../';

// What the API says about a group order, to whoever is asking. Guests only ever get their own items.
export type GroupLine = {
    line_id: string;
    // item_id, quantity and modifiers: what gets validated
    order: GroupLineOrder;
    // what the basket shows (see services/groupOrder)
    display: GroupLineDisplay;
    // who added it
    participant: string | null;
    mine: boolean;
    // the name of whatever is no longer orderable, when the menu has moved on
    problem: string | null;
};

export type GroupLineOrder = {
    item_id: string;
    quantity: number;
    modifiers: { modifier_id: string; quantity: number }[];
};

export type GroupLineDisplay = {
    name: string;
    price: number;
    modifiersTotal: number;
    modifiers: { uid: string; name: string; quantity: number; group_uid: string }[];
};

export type GroupStatus = 'open' | 'locked' | 'placed' | 'cancelled';

export type GroupState = {
    group: {
        id: string;
        status: GroupStatus;
        site_uid: string;
        site_name: string;
        channel: string;
    };
    host: string | null;
    // not there until someone has joined
    me?: { name: string; is_lead: boolean };
    items?: GroupLine[];
    guests?: { name: string; items: number }[];
    revision?: string;
    confirmation?: {
        site_name: string;
        timeslot: string | null;
        ready_at: string | null;
        // where it's going, once the order exists
        address: string | null;
    };
};

export type GroupSession = GroupState & {
    token?: string;
    participant_token?: string;
};

export const getGroup = (token: string, participantToken?: string): Promise<GroupState> =>
    api
        .get('group', { params: { token, ...(participantToken ? { participant_token: participantToken } : {}) } })
        .then((response) => handleResponse(response))
        .catch((error) => handleError(error));

export const postGroupStart = (siteUid: string, name: string): Promise<GroupSession> =>
    api
        .post('group/start', { uid: siteUid, name })
        .then((response) => handleResponse(response))
        .catch((error) => handleError(error));

export const postGroupJoin = (token: string, name: string): Promise<GroupSession> =>
    api
        .post('group/join', { token, name })
        .then((response) => handleResponse(response))
        .catch((error) => handleError(error));

export const postGroupItem = (
    token: string,
    participantToken: string,
    lineId: string,
    order: GroupLineOrder,
    display: GroupLineDisplay,
): Promise<GroupState> =>
    api
        .post('group/item', {
            token,
            participant_token: participantToken,
            line_id: lineId,
            order: JSON.stringify(order),
            display: JSON.stringify(display),
        })
        .then((response) => handleResponse(response))
        .catch((error) => handleError(error));

export const postGroupItemRemove = (token: string, participantToken: string, lineId: string): Promise<GroupState> =>
    api
        .post('group/item/remove', { token, participant_token: participantToken, line_id: lineId })
        .then((response) => handleResponse(response))
        .catch((error) => handleError(error));

export const postGroupLock = (token: string, participantToken: string, locked: boolean): Promise<GroupState> =>
    api
        .post('group/lock', { token, participant_token: participantToken, locked })
        .then((response) => handleResponse(response))
        .catch((error) => handleError(error));

export const postGroupCancel = (token: string, participantToken: string): Promise<GroupState> =>
    api
        .post('group/cancel', { token, participant_token: participantToken })
        .then((response) => handleResponse(response))
        .catch((error) => handleError(error));

export const postGroupLeave = (token: string, participantToken: string): Promise<{ left: boolean }> =>
    api
        .post('group/leave', { token, participant_token: participantToken })
        .then((response) => handleResponse(response))
        .catch((error) => handleError(error));
